// lib/services/shader/shader_service.ts

"use client";

type ShaderDef = {
  name: string;
  headers: string[];
};

function SHADER(name: string, ...headers: string[]): ShaderDef {
  return { name, headers };
}

export default class ShaderService {
  private device: GPUDevice | null = null;
  private adapter: GPUAdapter | null = null;

  private header_sources: Map<string, string> = new Map();
  private compiled_compute: Map<string, GPUShaderModule> = new Map();
  private compiled_render: Map<string, GPUShaderModule> = new Map();

  private initialized = false;

  private header_names: string[] = [
    "animation_types",
    "colortypes",
    "circle_helpers_nv12",
    "circle_helpers_i420",
    "circle_helpers",
    "trigtables360",
    "nv12_helpers",
    "i420_helpers",
    "draw_helpers",
    "lookhelpers",
    "look_2x2",
    "look_3x3",
    "local_bias",
    "black_and_white_helpers"
  ];

  private compute_defs: ShaderDef[] = [
    SHADER("animation_controller", "animation_types"),
   /* SHADER("black_white_grid_nv12","colortypes"),
    SHADER("black_white_grid_i420","colortypes"),
    SHADER("black_white_grid_reduce","colortypes"),
    SHADER("black_white_mask_a_nv12","colortypes","black_and_white_helpers"),
    SHADER("black_white_mask_a_i420","colortypes","black_and_white_helpers"),
    SHADER("black_white_mask_b_nv12","colortypes","black_and_white_helpers"),
    SHADER("black_white_mask_b_i420","colortypes","black_and_white_helpers"),
    SHADER("clear_blobs_basic","colortypes"),
    SHADER("clear_blobs","colortypes"),
    SHADER("clear_sticker_result","colortypes"),
    SHADER("clearblobtex","colortypes"),
    SHADER("composebloblista","colortypes"),
    SHADER("composebloblistb","colortypes"),
    SHADER("fillblobsmapx_bw","colortypes"),
    SHADER("fillblobsmapy_bw","colortypes"),
    SHADER("filterblobscontrast_nv12","colortypes"),
    SHADER("filterblobscontrast_i420","colortypes"),
    SHADER("filterblobsneighbors","colortypes"),
    SHADER("findnearestneighbor3x3","colortypes"),
    SHADER("get_dirangle_and_colors_nv12","colortypes","circle_helpers","circle_helpers_nv12","nv12_helpers","trigtables360","local_bias"),
    SHADER("get_dirangle_and_colors_i420","colortypes","circle_helpers","circle_helpers_i420","i420_helpers","trigtables360","local_bias"),
    SHADER("lookforstickersall","colortypes","lookhelpers","look_2x2","look_3x3"),
    SHADER("debug_blob_basic","colortypes","draw_helpers"),
    SHADER("debug_blob","colortypes","draw_helpers"),
    SHADER("debug_blob_dirangle","colortypes","draw_helpers"),
    SHADER("pack_blobs","colortypes"),*/
       

  ];

  private render_defs: ShaderDef[] = [
    SHADER("grid_shader"),
    SHADER("canvas_color"),
    SHADER("unlit_texture"),
    SHADER("unlit_texture_skinned"),
    SHADER("papercraft_skinned"),
    SHADER("blob_shadow"),
    SHADER("collision_shader"),
    SHADER("graph_shader"),
    SHADER("papercraft_static"),
    SHADER("unlit_texture_alpha"),
    SHADER("unlit_texture_alpha_instanced"),
    SHADER("unlit_no_discard"),
    SHADER("storage_color"),
    SHADER("storage_bw"),
    SHADER("storage_blob"),
    SHADER("nv12_color"),
    SHADER("i420_color"),
    SHADER("nv12_blob"),
    SHADER("i420_blob"),
    SHADER("video_yuv_rgb_NV12"),
    SHADER("video_yuv_rgb_I420"),
    SHADER("scrim"),
    SHADER("scan_frame"),
    SHADER("scan_corner"),
    SHADER("debug_texture"),
    SHADER("debug_blob_overlay_nv12"),
    SHADER("debug_blob_overlay_i420"),
    SHADER("scan_feedback"),

  ];

  constructor() {}

  async init() {
    if (this.initialized) return;

    this.adapter = await navigator.gpu.requestAdapter();
    if (this.adapter) {
      this.device = await this.adapter.requestDevice();
    }

    if (this.device) {
      await this.load_headers();
      await this.compile_compute_shaders();
      await this.compile_render_shaders();
    }

    this.initialized = true;
  }

  private async load_headers(): Promise<void> {
    for (const header_name of this.header_names) {
      const source = await this.fetchShader(header_name + ".wgsl");
      this.header_sources.set(header_name, source);
    }
  }

  private async compile_compute_shaders(): Promise<void> {
    if (!this.device) return;

    for (const def of this.compute_defs) {
      const shader_source = await this.fetchShader(def.name + ".wgsl");
      const full_source = this.assemble_source(def, shader_source);

      const shader_module = this.device.createShaderModule({
        label: def.name,
        code: full_source,
      });

      this.compiled_compute.set(def.name, shader_module);
    }
  }

  private async compile_render_shaders(): Promise<void> {
    if (!this.device) return;

    for (const def of this.render_defs) {
      const shader_source = await this.fetchShader(def.name + ".wgsl");
      const full_source = this.assemble_source(def, shader_source);

      const shader_module = this.device.createShaderModule({
        label: def.name,
        code: full_source,
      });

      this.compiled_render.set(def.name, shader_module);
    }
  }

  private assemble_source(def: ShaderDef, shader_source: string): string {
    if (def.headers.length === 0) {
      return shader_source;
    }

    const header_chunks: string[] = [];

    for (const header_name of def.headers) {
      const header_source = this.header_sources.get(header_name);
      if (!header_source) {
        throw new Error(
          `Shader "${def.name}" requires header "${header_name}" but the header was not loaded. ` +
            `Add "${header_name}" to the header_names list in ShaderService.`
        );
      }
      header_chunks.push(header_source);
    }

    return header_chunks.join("\n\n") + "\n\n" + shader_source;
  }

  async fetchShader(name: string): Promise<string> {
    const res = await fetch(`/api/shader/${encodeURIComponent(name)}`, {
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error(`Failed to load shader: ${name}`);
    }

    return await res.text();
  }

  toJSON() {
    const compute_list = [];
    for (const [key, value] of this.compiled_compute) {
      compute_list.push({ shader: key, compiled: !!value });
    }

    const render_list = [];
    for (const [key, value] of this.compiled_render) {
      render_list.push({ shader: key, compiled: !!value });
    }

    const header_list = [];
    for (const [key, value] of this.header_sources) {
      header_list.push({ header: key, loaded: !!value });
    }

    return { headers: header_list, compute: compute_list, render: render_list };
  }

  get_device(): GPUDevice | null {
    return this.device;
  }

  get(name: string): GPUShaderModule {
    if (!this.initialized || !this.device) {
      throw new Error("ShaderService not initialized. Call init() first.");
    }
    const mod = this.compiled_compute.get(name);
    if (!mod) {
      throw new Error(`Compute shader "${name}" not found.`);
    }
    return mod;
  }

  get_render(name: string): GPUShaderModule {
    if (!this.initialized || !this.device) {
      throw new Error("ShaderService not initialized. Call init() first.");
    }
    const mod = this.compiled_render.get(name);
    if (!mod) {
      throw new Error(`Render shader "${name}" not found.`);
    }
    return mod;
  }
}